import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Heart, Bell, Search, Sparkles, User, Store, FileText, ArrowRight, MapPin, GraduationCap, Briefcase, Check, TrendingUp, Users, ShieldCheck, SlidersHorizontal, Lock, Smartphone } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import MatchBoxModal from "../components/MatchBoxModal";
import HandoffModal from "../components/HandoffModal";
import CompatibilityRing from "../components/CompatibilityRing";
import UpdateKundaliModal from "../components/UpdateKundaliModal";
import api, { authStore } from "../lib/api";

/**
 * Post-onboarding Home Hub — mirrors bharat-register mobile app home tab.
 * Sections: my-profile summary, stats strip, alerts, match grid, quick actions.
 * Mock matches + alerts are code-level constants (purged at go-live).
 */
const InitialAvatar = ({ letter, size = 56 }) => (
  <div className="rounded-full flex items-center justify-center font-serif"
    style={{ width: size, height: size, background: "linear-gradient(135deg, rgba(200,162,74,0.20), rgba(122,14,24,0.15))",
             border: "1px solid var(--gold)", color: "var(--crimson)", fontSize: Math.round(size * 0.42) }}>
    {letter || "?"}
  </div>
);

const StatTile = ({ icon: Icon, label, value, accent }) => (
  <div className="editorial-card p-4 flex items-center gap-3">
    <span className="inline-flex items-center justify-center w-10 h-10"
      style={{ background: "rgba(122, 14, 24, 0.06)", border: "1px solid var(--gold)" }}>
      <Icon size={16} style={{ color: accent || "var(--crimson)" }}/>
    </span>
    <div>
      <p className="font-serif text-xl" style={{ color: "var(--ink)", lineHeight: 1 }}>{value}</p>
      <p className="text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--ash)" }}>{label}</p>
    </div>
  </div>
);

const QuickAction = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="editorial-card p-5 h-full text-left flex flex-col items-start hover:-translate-y-0.5 transition-transform">
    <span className="inline-flex items-center justify-center w-10 h-10 mb-3"
      style={{ background: "rgba(200, 162, 74, 0.12)", border: "1px solid var(--gold)" }}>
      <Icon size={18} style={{ color: "var(--crimson)" }}/>
    </span>
    <p className="font-serif text-base" style={{ color: "var(--ink)" }}>{label}</p>
    <ArrowRight size={14} className="mt-2" style={{ color: "var(--crimson)" }}/>
  </button>
);

export default function Home() {
  const nav = useNavigate();
  const [feed, setFeed] = useState(null);
  const [sent, setSent] = useState({});
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [unlockedIds, setUnlockedIds] = useState({});
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [kundaliUpdateOpen, setKundaliUpdateOpen] = useState(false);

  useEffect(() => {
    if (!authStore.token()) { nav("/"); return; }
    (async () => {
      try {
        const { data } = await api.get("/home/feed");
        setFeed(data);
      } catch {
        authStore.clear();
        nav("/");
      }
    })();
  }, [nav]);

  const logout = () => { authStore.clear(); nav("/"); };

  const sendInterest = async (matchId, name) => {
    if (sent[matchId]) return;
    try {
      await api.post("/home/interest", { target_match_id: matchId });
      setSent((s) => ({ ...s, [matchId]: true }));
      toast.success(`Interest sent to ${name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send interest");
    }
  };

  if (!feed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ivory)" }}>
        <p className="eyebrow">Loading your dashboard…</p>
      </div>
    );
  }

  const { my_profile: me, stats, matches, alerts } = feed;

  return (
    <div data-testid="home-dashboard" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      {/* Header */}
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <div className="flex items-center gap-4">
          <button onClick={() => setHandoffOpen(true)} data-testid="home-handoff"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--crimson)" }}>
            <Smartphone size={14}/> Continue on Mobile
          </button>
          <button onClick={logout} data-testid="home-logout"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <LogOut size={14}/> Logout
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-10 pt-10">
        <div className="max-w-6xl mx-auto flex items-center gap-5">
          <InitialAvatar letter={(me.name || "V")[0]?.toUpperCase()} size={72}/>
          <div>
            <div className="eyebrow">Namaste</div>
            <h1 className="font-serif text-3xl md:text-4xl mt-1" style={{ color: "var(--crimson)" }} data-testid="home-greeting">
              {me.name || "Welcome back"}
            </h1>
            {me.city && (
              <p className="mt-1 text-sm inline-flex items-center gap-1" style={{ color: "var(--ash)" }}>
                <MapPin size={12}/> {me.city} {me.gotra && `· Gotra ${me.gotra}`}
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              {me.identity_verified ? (
                <span data-testid="okyc-badge-verified" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                  style={{ background: "rgba(85,127,74,0.10)", color: "var(--leaf)", border: "1px solid var(--leaf)" }}>
                  <ShieldCheck size={11}/> Aadhaar Verified
                </span>
              ) : (
                <span data-testid="okyc-badge-pending" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                  style={{ background: "rgba(122,14,24,0.06)", color: "var(--crimson)", border: "1px solid var(--crimson)" }}>
                  <Lock size={11}/> Aadhaar OKYC pending
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="px-6 md:px-10 mt-8" data-testid="home-stats">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile icon={TrendingUp} label="Profile Completion" value={`${stats.profile_completion_pct}%`}/>
          <StatTile icon={Users}      label="Matches Found"      value={stats.matches_count}/>
          <StatTile icon={Heart}      label="Interests Received" value={stats.interests_received}/>
          <StatTile icon={Bell}       label="Alerts"             value={stats.alerts_unread}/>
        </div>
      </section>

      {/* Alerts */}
      <section className="px-6 md:px-10 mt-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow flex items-center gap-2"><Bell size={12}/> Alerts</div>
              <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>Recent Activity</h2>
            </div>
          </div>
          <div className="mt-6 divide-y" style={{ borderColor: "var(--line)" }} data-testid="home-alerts">
            {alerts.map((a) => (
              <div key={a.id} className="py-4 flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full" style={{ background: "var(--crimson)", flexShrink: 0 }}/>
                <div className="flex-1">
                  <p className="font-serif text-base md:text-lg" style={{ color: "var(--ink)" }}>{a.title}</p>
                  <p className="text-sm mt-1" style={{ color: "var(--ash)" }}>{a.body}</p>
                  <p className="text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--ash)" }}>{a.when}</p>
                </div>
                <button className="text-[11px] tracking-widest uppercase" style={{ color: "var(--crimson)" }}>{a.cta} →</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="px-6 md:px-10 mt-12">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow">Shortcuts</div>
          <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>Manage Your Journey</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickAction testid="qa-profile" icon={User}      label="My Profile"           onClick={() => nav("/profile")}/>
            <QuickAction testid="qa-preferences" icon={SlidersHorizontal} label="Preferences" onClick={() => nav("/preferences")}/>
            <QuickAction testid="qa-vendors" icon={Store}     label="Marriage Services"    onClick={() => nav("/vendors")}/>
            <QuickAction testid="qa-update-kundali" icon={FileText}  label="Update Kundali Details" onClick={() => setKundaliUpdateOpen(true)}/>
          </div>
        </div>
      </section>

      {/* Match grid */}
      <section className="px-6 md:px-10 mt-14 mb-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between">
            <div>
              <div className="eyebrow flex items-center gap-2"><Sparkles size={12}/> Handpicked</div>
              <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>Your Matches</h2>
              <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
                Aadhaar-verified profiles tailored to your gotra, kul and preferences.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-match-grid">
            {matches.map((m) => (
              <article key={m.id} data-testid={`match-${m.id}`} className="editorial-card p-6 flex flex-col h-full">
                <div className="flex items-start justify-between">
                  <InitialAvatar letter={m.photo_initial}/>
                  <CompatibilityRing
                    guna={m.guna_score}
                    gunaMax={m.guna_max || 36}
                    pct={m.compat_pct ?? m.match_percent}
                    verdict={m.compat_verdict}
                    size={68}
                  />
                </div>
                <h3 className="font-serif text-xl mt-4" style={{ color: "var(--ink)" }}>
                  {m.name}, <span style={{ color: "var(--ash)", fontWeight: 400 }}>{m.age}</span>
                </h3>
                <p className="mt-1 text-sm inline-flex items-center gap-1" style={{ color: "var(--ash)" }}>
                  <MapPin size={12}/> {m.city} · {m.caste} · {m.gotra}
                </p>
                <div className="mt-3 space-y-1 text-xs" style={{ color: "var(--ash)" }}>
                  <p className="flex items-center gap-1.5"><Briefcase size={12}/> {m.profession}</p>
                  <p className="flex items-center gap-1.5"><GraduationCap size={12}/> {m.education}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="text-[10px] tracking-widest uppercase px-2 py-1"
                    style={{ background: "rgba(200, 162, 74, 0.10)", border: "1px solid rgba(200, 162, 74, 0.35)", color: "var(--ink)" }}>
                    {m.height_cm} cm
                  </span>
                  <span className="text-[10px] tracking-widest uppercase px-2 py-1"
                    style={{ background: "rgba(200, 162, 74, 0.10)", border: "1px solid rgba(200, 162, 74, 0.35)", color: "var(--ink)" }}>
                    Manglik · {m.manglik}
                  </span>
                </div>
                <div className="mt-auto pt-5 flex items-center justify-between" style={{ borderTop: "1px solid var(--line)", marginTop: "1.25rem" }}>
                  {sent[m.id] ? (
                    <span data-testid={`sent-${m.id}`} className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>
                      <Check size={14}/> Interest Sent
                    </span>
                  ) : (
                    <button data-testid={`interest-${m.id}`} onClick={() => sendInterest(m.id, m.name)}
                      className="btn-primary text-[11px] py-2 px-3">
                      Express Interest <Heart size={12}/>
                    </button>
                  )}
                  <button data-testid={`reveal-${m.id}`} onClick={() => setUnlockTarget({ id: m.id, name: m.name })}
                    className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase"
                    style={{ color: unlockedIds[m.id] ? "var(--leaf)" : "var(--crimson)" }}>
                    {unlockedIds[m.id] ? <><Check size={12}/> Unlocked</> : <><Lock size={12}/> Reveal Contact</>}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <MatchBoxModal
        open={!!unlockTarget}
        target={unlockTarget}
        onClose={() => setUnlockTarget(null)}
        onUnlocked={(u) => setUnlockedIds((prev) => ({ ...prev, [u.target_profile_id]: u }))}
      />
      <HandoffModal open={handoffOpen} onClose={() => setHandoffOpen(false)} destinationPath="/home"/>
      <UpdateKundaliModal
        open={kundaliUpdateOpen}
        initial={{
          dob: feed?.my_profile?.dob,
          birth_time: feed?.my_profile?.birth_time,
          birth_place: feed?.my_profile?.birth_place,
        }}
        onClose={() => setKundaliUpdateOpen(false)}
      />
    </div>
  );
}
